import { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { ExternalLink, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Overline from "@/components/Overline";
import { activeProducts } from "@/data/products";
import { company, seo, buildQuoteUrl, buildEmbedUrl } from "@/config/site";

/**
 * Request-a-Quote page — embeds the configured Google Form.
 * When a `product` query param is present AND the form's product
 * entry ID is configured (see /src/config/site.js), the form is
 * loaded with that product pre-selected.
 */
export default function RequestQuote() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const preselect = params.get("product") || "";

  const embedUrl = buildEmbedUrl(preselect);
  const openUrl = buildQuoteUrl(preselect);

  return (
    <>
      <SEO
        title="Request a Quote"
        description="Share your packaging requirement — product, dimensions and quantity — and our team will follow up."
        canonical={`${seo.siteUrl}/request-a-quote`}
      />

      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Overline>Request a Quote</Overline>
          <h1 className="font-heading mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900">Tell us your requirement.</h1>
          <p className="mt-5 max-w-2xl text-neutral-600 leading-relaxed">
            Share your product, dimensions, quantity or requirement and our team will discuss a suitable packaging solution.
            {preselect ? (
              <>
                {" "}Product pre-selected: <strong className="text-neutral-900">{preselect}</strong>.
              </>
            ) : null}
          </p>

          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#A91D22] hover:text-[#8B181C] font-medium"
              data-testid="quote-open-new-tab"
            >
              Open form in a new tab <ExternalLink size={14} />
            </a>
            <span className="text-neutral-300">·</span>
            <a href={company.phoneHref} className="text-neutral-700 hover:text-[#A91D22] font-medium">Call {company.phone}</a>
            {company.email ? (
              <>
                <span className="text-neutral-300">·</span>
                <a href={`mailto:${company.email}`} className="text-neutral-700 hover:text-[#A91D22] font-medium">Email {company.email}</a>
              </>
            ) : null}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-neutral-200 shadow-sm rounded-sm overflow-hidden">
            <div className="border-b border-neutral-200 bg-neutral-50 px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-neutral-500">
              Powered by Google Forms · Secure submission
            </div>
            <iframe
              src={embedUrl}
              title="Rudraksh CS enquiry form"
              className="gform-embed"
              loading="lazy"
              data-testid="quote-iframe"
            >
              Loading…
            </iframe>
            <div className="border-t border-neutral-200 bg-neutral-50 px-5 py-4 text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-1">
              <span>Having trouble with the embedded form?</span>
              <a href={openUrl} target="_blank" rel="noopener noreferrer" className="text-[#A91D22] hover:text-[#8B181C] font-medium">
                Open in a new tab
              </a>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/products" className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-[#A91D22]">
              Browse products while you're here <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
